import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import PhoneVideoSection from "./PhoneVideoSection";
import PhoneAudioSection from "./PhoneAudioSection";

const EASE = [0.16, 1, 0.3, 1];

export default function MediaSection() {
    const containerRef = useRef(null);
    const [headerPl, setHeaderPl] = useState(0);
    const [descMax, setDescMax] = useState(0);

    // Keep the section header flush with the left player title (which sits on
    // the left phone's edge and zooms with it at each breakpoint), and cap the
    // description's width to the width of the h2 text ("...the RIVVR").
    useEffect(() => {
        const mdq = window.matchMedia("(min-width: 768px)");
        const sync = () => {
            const container = containerRef.current;
            const title = document.querySelector(
                '[data-testid="video-header"]'
            );
            if (!container || !title) return;
            if (!mdq.matches) {
                setHeaderPl(0);
                setDescMax(0);
                return;
            }
            setHeaderPl(
                Math.max(
                    0,
                    Math.round(
                        title.getBoundingClientRect().left -
                            container.getBoundingClientRect().left
                    )
                )
            );
            const containerBox = container.getBoundingClientRect();
            const titleLeft = title.getBoundingClientRect().left;
            setDescMax(Math.round(containerBox.right - titleLeft));
        };
        sync();
        const t = setTimeout(sync, 400);
        window.addEventListener("resize", sync);
        mdq.addEventListener?.("change", sync);
        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", sync);
            mdq.removeEventListener?.("change", sync);
        };
    }, []);

    return (
        <section
            id="curation"
            className="relative px-4 pt-2 pb-[34px] sm:px-8 sm:pt-[28px] sm:pb-[16px] lg:px-12"
            data-testid="media-section"
        >
            <div
                ref={containerRef}
                className="mx-auto max-w-7xl 2xl:max-w-[88rem]"
            >
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="-mb-0.5 flex flex-col gap-3 sm:mb-3 sm:gap-3 md:mb-[11px]"
                    style={
                        headerPl
                            ? { paddingLeft: `${headerPl}px` }
                            : undefined
                    }
                >
                    <div>
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-neon sm:mb-3">
                            The Curation
                        </p>
                        <h2 className="font-display text-4xl font-black tracking-tight text-ivory sm:text-5xl">
                            Straight from{" "}
                            <span className="font-display font-black italic text-neon">
                                the RIVVR
                            </span>
                        </h2>
                    </div>
                    <p
                        className="text-sm leading-[1.4] text-dim max-sm:-translate-y-[6px]"
                        style={
                            descMax
                                ? { maxWidth: `${descMax}px` }
                                : undefined
                        }
                    >
                        If you love Music and Movies, RIVVR is the new independent distribution and discovery network you've been waiting for. Every Musical Artist, Song, Movie, TV Show is hand picked and curated by RIVVR's team of award-winning producer talent.{" "}
                        <br className="hidden lg:block" />
                        Delivering only the best, worthy of your attention.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 gap-6 sm:gap-4 md:grid-cols-2 md:gap-9 xl:gap-14 max-sm:mt-[10px]">
                    <motion.div
                        className="md:order-2"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
                    >
                        <PhoneAudioSection />
                    </motion.div>
                    <motion.div
                        className="md:order-1 max-sm:mt-[10px]"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                    >
                        <PhoneVideoSection />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
